import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { getKpiBatch } from '../requests/api';

const getData = (res) => res?.data ?? res;

const chartKey = (tech, kpiName) => `${tech}::${kpiName}`;

export const loadKpiData = createAsyncThunk(
	'kpiData/batch',
	async (charts, { rejectWithValue }) => {
		try { return getData(await getKpiBatch(charts)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

const kpiDataSlice = createSlice({
	name: 'kpiData',
	initialState: {
		charts:      [],
		loadingKeys: [],
		error:       null,
	},
	reducers: {
		clearKpiData(state) {
			state.charts      = [];
			state.loadingKeys = [];
		},
		// Remove charts for a specific tech — used when Compare tech changes
		clearTechData(state, action) {
			const tech        = action.payload;
			state.charts      = state.charts.filter(c => c.tech !== tech);
			state.loadingKeys = state.loadingKeys.filter(k => !k.startsWith(`${tech}::`));
		},
	},
	extraReducers: b => {
		b.addCase(loadKpiData.pending, (s, a) => {
			s.error = null;
			const keys = (a.meta.arg ?? []).map(c => chartKey(c.tech, c.kpiName));
			s.loadingKeys = [...new Set([...current(s.loadingKeys), ...keys])];
		});
		b.addCase(loadKpiData.rejected, (s, a) => {
			s.loadingKeys = [];
			s.error = a.payload;
		});
		b.addCase(loadKpiData.fulfilled, (s, a) => {
			const incoming = Array.from(a.payload?.charts ?? []).map(c => ({
				...c,
				tech:    c.tech    ?? c.Tech    ?? '',
				kpiName: c.kpiName ?? c.KpiName ?? '',
				unit:    c.unit    ?? c.Unit,
				error:   c.error   ?? c.Error,
				series:  (c.series ?? c.Series ?? []).map(s => ({
					...s,
					entityId: s.entityId ?? s.EntityId ?? '',
					dates:    s.dates    ?? s.Dates    ?? [],
					values:   s.values   ?? s.Values   ?? [],
				})),
			}));
			const keys     = incoming.map(c => chartKey(c.tech, c.kpiName));
			s.loadingKeys  = current(s.loadingKeys).filter(k => !keys.includes(k));
			const currentCharts = Array.from(current(s.charts));
			const merged = currentCharts.map(existing => {
				const update = incoming.find(n => n.tech === existing.tech && n.kpiName === existing.kpiName);
				return update ?? existing;
			});
			incoming.forEach(n => {
				if (!currentCharts.some(e => e.tech === n.tech && e.kpiName === n.kpiName))
					merged.push(n);
			});
			s.charts = merged;
		});
	},
});

export const { clearKpiData, clearTechData } = kpiDataSlice.actions;
export default kpiDataSlice.reducer;

export const selectChart = (tech, kpiName) => state =>
	state.kpiData.charts.find(c =>
		(c.tech ?? c.Tech) === tech && (c.kpiName ?? c.KpiName) === kpiName
	);

export const selectIsLoadingChart = (tech, kpiName) => state =>
	state.kpiData.loadingKeys.includes(chartKey(tech, kpiName));

export const selectIsLoadingTech = (tech) => state =>
	state.kpiData.loadingKeys.some(k => k.startsWith(`${tech}::`));
